import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import Patient, { patientSignInStatus } from '../model/Patient'
import PatientRepository from '../clients/db/PatientRepository'

import { AppThunk } from '../store'
import { getUserDetails } from 'user/UserApi'
interface PatientState {
  isLoading: boolean
  isUpdatedSuccessfully: boolean
  patient: Patient
  relatedPersons: Patient[]
  tempPass: string
  lockedByName: string
  lockedById: string
}

const initialState: PatientState = {
  isLoading: false,
  isUpdatedSuccessfully: false,
  patient: {} as Patient,
  relatedPersons: [],
  tempPass: '',
  lockedByName: '',
  lockedById: '',
}

function startLoading(state: PatientState) {
  state.isLoading = true
  state.lockedByName = ''
  state.lockedById = ''
  state.patient = {} as Patient
}

const patientSlice = createSlice({
  name: 'patient',
  initialState,
  reducers: {
    clearPatient: startLoading,
    fetchPatientStart: startLoading,
    createPatientStart: startLoading,
    updatePatientStart: startLoading,
    fetchPatientSuccess(state, { payload }: PayloadAction<Patient>) {
      state.patient = payload
      state.isLoading = false
    },
    fetchPatientSilentlySuccess(state, { payload }: PayloadAction<Patient>) {
      if (state.patient.id === payload.id) {
        state.patient = payload
      }
    },
    createPatientSuccess(state) {
      state.isLoading = false
    },
    updatePatientSuccess(state, { payload }: PayloadAction<Patient>) {
      state.isLoading = false
      state.patient = payload
    },
    setTempPass(state, { payload }: PayloadAction<string>) {
      state.tempPass = payload
    },
    updatePatientFailed(state: PatientState) {
      state.isLoading = false
    },
    lockedBy(state, { payload }: PayloadAction<{ patient: Patient; lockedByName: string }>) {
      if (state.patient.id === payload.patient.id) {
        state.lockedByName = payload.lockedByName
        state.lockedById = payload.patient.lockedBy
        state.patient = payload.patient
      }
    },
    removePatient(state: PatientState) {
      state.patient = {} as Patient
    },
  },
})

export const {
  clearPatient,
  fetchPatientStart,
  fetchPatientSuccess,
  fetchPatientSilentlySuccess,
  createPatientStart,
  createPatientSuccess,
  updatePatientStart,
  updatePatientSuccess,
  updatePatientFailed,
  setTempPass,
  lockedBy,
  removePatient,
} = patientSlice.actions

async function updateLockedBy(patient: Patient, dispatch: any) {
  const response = await getUserDetails(patient.lockedBy)
  dispatch(
    lockedBy({
      patient,
      lockedByName: response.data && response.data.name ? response.data.name : '',
    }),
  )
}

export const clearOldPatient = (): AppThunk => async dispatch => {
  dispatch(clearPatient())
}

export const fetchPatient = (id: string): AppThunk => async dispatch => {
  dispatch(fetchPatientStart())
  const patient = await PatientRepository.find(id)

  dispatch(fetchPatientSuccess(patient))
}

export const getPatientDetails = async (patientId: string) => {
  const patient = await PatientRepository.find(patientId)
  return patient
}

export const fetchPatientSilently = (id: string): AppThunk => async dispatch => {
  const patient = await PatientRepository.find(id)
  dispatch(fetchPatientSilentlySuccess(patient))
  await updateLockedBy(patient, dispatch)
}

export const createPatient = (
  patient: Patient,
  onSuccess?: (patient: Patient) => void,
): AppThunk => async dispatch => {
  dispatch(createPatientStart())
  patient.signInStatus = patientSignInStatus.waitingPassword

  const newPatient = await PatientRepository.save(patient)
  dispatch(createPatientSuccess())
  if (onSuccess) {
    onSuccess(newPatient)
  }
}

export const newPassword = (patient: Patient, history: any): AppThunk => async dispatch => {
  dispatch(updatePatientStart())
  PatientRepository.generatePassword().then(async response => {
    patient.password = response.data.hash
    const updatedPatient = await PatientRepository.saveOrUpdate(patient)
    dispatch(updatePatientSuccess(updatedPatient))
    history.push(`/patients/${updatedPatient.id}`)
    dispatch(setTempPassword(response.data.password))
  })
}

export const updatePatient = (
  patient: Patient,
  onSuccess?: (patient: Patient) => void,
  onError?: (error: any) => void,
): AppThunk => async dispatch => {
  dispatch(updatePatientStart())

  try {
    const updatedPatient = await PatientRepository.saveOrUpdate(patient)

    dispatch(updatePatientSuccess(updatedPatient))
    if (onSuccess) {
      onSuccess(updatedPatient)
    }
  } catch (error) {
    dispatch(updatePatientFailed())

    if (onError) {
      onError(error)
    }
  }
}

export const updatePatientSilently = (
  patient: Patient,
  onSuccess?: (patient: Patient) => void,
  onError?: (error: any) => void,
): AppThunk => async dispatch => {
  try {
    const updatedPatient = await PatientRepository.saveOrUpdate(patient)
    dispatch(updatePatientSuccess(updatedPatient))
    if (onSuccess) {
      onSuccess(updatedPatient)
    }
  } catch (error) {
    if (onError) {
      onError(error)
    }
  }
}

export const lockPatient = (
  patient: Patient,
  onSuccess?: (patient: Patient) => void,
  onError?: (error: any) => void,
): AppThunk => async dispatch => {
  try {
    const updatedPatient = await PatientRepository.saveOrUpdate(patient)
    if (onSuccess) {
      onSuccess(updatedPatient)
    }
    await updateLockedBy(updatedPatient, dispatch)
  } catch (error) {
    dispatch(fetchPatientSilently(patient.id))
    if (onError) {
      onError(error)
    }
  }
}

export const unlockPatient = (patient: Patient): AppThunk => async () => {
  await PatientRepository.saveOrUpdate(patient)
}

export const setTempPassword = (tempPass: string): AppThunk => dispatch => {
  dispatch(setTempPass(tempPass))
}

export default patientSlice.reducer
